import { Typography } from "antd";
import _ from "lodash";
import { marked } from "marked";
import React, { useEffect, useState } from 'react';
import { transformComment } from "utils/commentUtil";
import { ATTACHMENT_LINK_PREFIX } from "views/Findings/Constants";
import parseHtml from "utils/parserUtil";

const baseRenderer = new marked.Renderer();

// Custom renderer overrides for marked v17 — only override methods that need customization
export const renderer = {
    link(token) {
        const { href, text } = token;
        if (!href) return text || "";

        const anchorText = baseRenderer.link.call(this, token);
        const isAttachment = href.startsWith(ATTACHMENT_LINK_PREFIX);
        const name = text || href;

        if (anchorText.startsWith("<a")) {
            if (isAttachment) {
                const link = href.replace(ATTACHMENT_LINK_PREFIX, "");
                return anchorText
                    .replace("<a", `<div class="ql-attachment" data-url="${link}" data-name="${name}" data-type="attachment"`)
                    .replace("</a>", "</div>");
            }
            return anchorText.replace("<a", "<a target='_blank'");
        }

        return anchorText;
    },

    paragraph(token) {
        let paragraphText = this.parser.parseInline(token.tokens);
        paragraphText = checkForWidthHeightQueryParams(paragraphText);
        if (paragraphText.trim().startsWith("<img") || paragraphText.trim().startsWith("<file-attachment")) {
            return paragraphText + "\n";
        }
        return `<p>${paragraphText}</p>`;
    },

    del(token) {
        const strikeThroughText = baseRenderer.del.call(this, token);
        return strikeThroughText.replaceAll("del>", "s>");
    },

    code({ text }) {
        return `<pre><code>${text}</code></pre>`;
    }
};

// Configure marked with options and custom renderer
marked.use({
    breaks: true,
    gfm: true,
    renderer
});

const checkForWidthHeightQueryParams = (html) => {
    // eslint-disable-next-line no-undef
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
  
    // Find all images in the HTML
    const imgs = doc.querySelectorAll("img");
  
    imgs.forEach((img) => {
      const originalSrc = img.getAttribute('src');
  
      if (originalSrc) {
        const url = new URL(originalSrc, window.location.origin);
        const width = url.searchParams.get('width');
        const height = url.searchParams.get('height');
        img.setAttribute('width', width);
        img.setAttribute('height', height);
      }
    });
  
    // Return the modified HTML
    return doc.body.innerHTML;
}

const regExp = new RegExp(/^[#]+\w/gm);

export const MarkdownFormatter = props => {
    const [text, setText] = useState(props.markdown);

    const [expanded, setExpanded] = useState(false);

    const onExpand = (event) => {
        setExpanded(true)
    }

    useEffect(() => {
        // split text with new line
        const text = parseJiraLinkFormats(props.markdown).split("\n");
        const lines = [];

        // Find line with #sometext
        // Add space after '#' in order to markup the heading
        for(const line of text) {
            if(line.match(regExp)) {
                const noOfHash = line.lastIndexOf("#") + 1;
                
                const hashs = line.slice(0, line.lastIndexOf("#") + 1)
                const trailingString = line.slice(noOfHash)
    
                lines.push(hashs + " " + trailingString)
            } else {
                lines.push(line)
            }
        }
        // Add newline character after each line
        setText(lines.join(String.fromCharCode(13)))

    }, [props.markdown])

    const markedResult = marked.parse(text);
    const markedDown = (
        <div className="markdown-text" >
            {parseHtml(markedResult, { transform: transformComment, trim: true })}
        </div>
    );
    
    const htmlMarkDown =(htmlText)=>{
        const _text = document.createElement('div');
        _text.innerHTML = htmlText;
        return _text.innerText
    }
    return (
        props.collapsible ? 
            <Typography.Paragraph className="m-b-0" key={Number(expanded)} ellipsis={expanded ? false: { 
                rows: 1, 
                expandable: false, 
                suffix: <Typography.Link onClick={onExpand}> ...Show more</Typography.Link>
            }} >
                {
                    !expanded ? htmlMarkDown(props.markdown) 
                        : <>{markedDown}<br/> <Typography.Link onClick={()=> setExpanded(false)}>...Show Less</Typography.Link></>
                }
            </Typography.Paragraph>
            :
            markedDown
    );
};


// Jira uses a different syntax for link - [text|http://example.com]
// this method converts the jira format to markdown format - [text](http://example.com)
export function parseJiraLinkFormats(text){
    const regex = /\[.*?\]/g;
    const matches = text.match(regex);
    if(!matches){
        return text;
    }
    _.forEach(matches, match => {
        const array = match.substring(1, match.length).substring(0, match.length - 2).split("|");
        if(array.length !== 2){
            return;
        }
        const [linkText, link] = array;
        text = text.replaceAll(match, `[${linkText}](${link})`);
    });
    return text;
}